/**
 * Leitura das estações no instante do quadro exibido — funções puras
 * (testadas em `meteo.test.ts`).
 *
 * A regra é a mesma do nível dos rios e dos pluviômetros: **o que não se
 * mediu não aparece**. Uma estação calada neste horário fica vazada no mapa,
 * nunca com zero nem com o valor de ontem.
 *
 * Duas escolhas moldam o arquivo:
 *
 * 1. **A observação vale da hora dela para a frente, nunca para trás.** O
 *    quadro das 14:40 mostra a observação das 14:00, e não a das 15:00: esta
 *    ainda não existia às 14:40. (Os rios arredondam para a hora mais
 *    próxima: a cota muda devagar; a temperatura da tarde, não.)
 * 2. **Até 2 h de idade.** O INMET publica a hora HH por volta de HH+60 min,
 *    então o quadro mais novo do satélite quase sempre é mais novo que a
 *    última hora publicada. Até 2 h a observação aparece (esmaecida depois de
 *    1 h, e a ficha diz a idade); mais velha que isso, a estação fica vazada.
 */
import type { Chave, Dados, Estacao, Leitura, SerieMetar, Variavel } from './types';

export const HORA_MS = 3_600_000;
/** Idade máxima de uma observação para ela valer no quadro. */
export const TOLERANCIA_MS = 2 * HORA_MS;
/** Daqui em diante a observação é "defasada": aparece esmaecida. */
export const DEFASADA_MS = HORA_MS;

export const CHAVES: readonly Chave[] = ['t', 'td', 'ur', 'vv', 'dv', 'raj', 'p', 'ps', 'chuva', 'vis'];

/** Leitura da série horária do INMET no slot `i`, ou `null` se nada foi medido nele. */
export function leituraDoSlot(e: Estacao, i: number, t0: number, passoMin: number): Leitura | null {
  const h = e.horaria;
  if (!h) return null;
  const valores: Partial<Record<Chave, number>> = {};
  let algum = false;
  for (const k of CHAVES) {
    const v = h[k]?.[i];
    if (typeof v === 'number' && Number.isFinite(v)) {
      valores[k] = v;
      algum = true;
    }
  }
  return algum ? { ms: t0 + i * passoMin * 60_000, valores } : null;
}

/** Leitura do METAR de índice `i`. */
export function leituraMetar(s: SerieMetar, i: number): Leitura | null {
  const ms = s.ms[i];
  if (ms === undefined) return null;
  const valores: Partial<Record<Chave, number>> = {};
  for (const k of CHAVES) {
    const v = s.valores[k]?.[i];
    if (typeof v === 'number' && Number.isFinite(v)) valores[k] = v;
  }
  return { ms, valores, wx: s.wx[i] ?? null, raw: s.raw[i] ?? null, speci: s.speci[i] ?? false };
}

/**
 * Última observação até `instante` (inclusive), sem limite de idade; `null`
 * sem nenhuma. Com `instante` nulo, a mais recente da estação.
 */
export function ultimaAte(e: Estacao, dados: Pick<Dados, 't0' | 'passoMin' | 'slots'>, instante: number | null): Leitura | null {
  if (e.rede === 'm') {
    const s = e.metar;
    if (!s || s.ms.length === 0) return null;
    let i = s.ms.length - 1;
    if (instante !== null) while (i >= 0 && s.ms[i]! > instante) i--;
    return i >= 0 ? leituraMetar(s, i) : null;
  }
  const passo = dados.passoMin * 60_000;
  if (!e.horaria || !(passo > 0)) return null;
  let i = dados.slots - 1;
  if (instante !== null) {
    if (!Number.isFinite(instante)) return null;
    // `floor`: a observação das 15:00 não vale para o quadro das 14:40.
    const k = Math.floor((instante - dados.t0) / passo + 1e-9);
    if (k < 0) return null;
    i = Math.min(k, dados.slots - 1);
  }
  for (; i >= 0; i--) {
    const l = leituraDoSlot(e, i, dados.t0, dados.passoMin);
    if (l) return l;
  }
  return null;
}

/**
 * A observação que o mapa pinta no quadro: a última até o instante, com no
 * máximo `TOLERANCIA_MS` de idade. Sem instante, a mais recente.
 */
export function leituraNoQuadro(
  e: Estacao,
  dados: Pick<Dados, 't0' | 'passoMin' | 'slots'>,
  instante: number | null,
): Leitura | null {
  const l = ultimaAte(e, dados, instante);
  if (!l) return null;
  if (instante !== null && instante - l.ms > TOLERANCIA_MS) return null;
  return l;
}

/** Idade da observação em relação ao quadro, em ms (0 sem quadro). */
export function idadeMs(l: Leitura, instante: number | null): number {
  return instante === null ? 0 : Math.max(0, instante - l.ms);
}

/** Vento de m/s para km/h, como o noticiário fala. */
export function kmh(ms: number): number {
  return ms * 3.6;
}

/**
 * Valor da variável do mapa numa leitura, na unidade da TELA (vento em km/h),
 * ou `null` quando ela não foi medida.
 */
export function valorDe(l: Leitura | null, variavel: Variavel): number | null {
  if (!l) return null;
  const v = l.valores;
  switch (variavel) {
    case 'vento':
      return v.vv === undefined ? null : kmh(v.vv);
    case 't':
    case 'ur':
    case 'p':
    case 'td':
    case 'chuva':
      return v[variavel] ?? null;
  }
}

// ---------------------------------------------------------------------------
// Séries para os gráficos da ficha (48 fatias de 1 h)
// ---------------------------------------------------------------------------

/**
 * Série de uma grandeza na grade horária. INMET: a própria. METAR: em cada
 * hora, a observação mais próxima da hora cheia (a até 30 min dela) — o
 * METAR de rotina é na hora cheia, e o SPECI no meio da hora não empurra a
 * linha para o lado. Hora sem observação: `null`.
 */
export function serieDe(e: Estacao, chave: Chave, t0: number, passoMin: number, slots: number): (number | null)[] {
  const out: (number | null)[] = new Array(slots).fill(null);
  if (e.rede === 'i') {
    const s = e.horaria?.[chave];
    if (s) for (let i = 0; i < slots; i++) out[i] = typeof s[i] === 'number' ? s[i]! : null;
    return out;
  }
  const s = e.metar;
  const col = s?.valores[chave];
  if (!s || !col) return out;
  const passo = passoMin * 60_000;
  const dist: number[] = new Array(slots).fill(Infinity);
  for (let j = 0; j < s.ms.length; j++) {
    const v = col[j];
    if (typeof v !== 'number') continue;
    const i = Math.round((s.ms[j]! - t0) / passo);
    if (i < 0 || i >= slots) continue;
    const d = Math.abs(s.ms[j]! - (t0 + i * passo));
    if (d <= passo / 2 && d < dist[i]!) {
      dist[i] = d;
      out[i] = v;
    }
  }
  return out;
}

/** Hora da grade que corresponde ao quadro exibido (mesma regra do mapa), ou -1. */
export function indiceNoQuadro(slots: number, t0: number, passoMin: number, instante: number | null): number {
  if (slots <= 0 || instante === null || !Number.isFinite(instante) || !(passoMin > 0)) return -1;
  const i = Math.floor((instante - t0) / (passoMin * 60_000) + 1e-9);
  return i >= 0 && i < slots ? i : -1;
}

/** Trechos contínuos (índices consecutivos com valor): a linha se interrompe no buraco. */
export function trechos(serie: readonly (number | null)[]): number[][] {
  const out: number[][] = [];
  let atual: number[] = [];
  serie.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) atual.push(i);
    else if (atual.length) {
      out.push(atual);
      atual = [];
    }
  });
  if (atual.length) out.push(atual);
  return out;
}

/** Faixa vertical de um gráfico: menor e maior valor medidos, com um piso de amplitude. */
export function faixa(series: readonly (readonly (number | null)[])[], piso: number): { min: number; max: number; base: number; amplitude: number } | null {
  const vals = series.flatMap((s) => s.filter((v): v is number => typeof v === 'number' && Number.isFinite(v)));
  if (vals.length === 0) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const amplitude = Math.max(piso, max - min);
  return { min, max, base: (min + max) / 2 - amplitude / 2, amplitude };
}

/** Rajada que merece ponto no gráfico: pelo menos isto acima do vento médio da hora (km/h). */
export const RAJADA_FORTE_KMH = 10;

/**
 * Só as rajadas fortes (≥ vento médio + `RAJADA_FORTE_KMH`), em km/h; as
 * outras horas ficam `null` no desenho — a leitura da hora mostra todas.
 * Sem vento médio na hora, a rajada não tem com o que se comparar e fica.
 */
export function rajadasFortes(vv: readonly (number | null)[], raj: readonly (number | null)[]): (number | null)[] {
  return raj.map((r, i) => {
    if (r === null) return null;
    const v = vv[i];
    return v === null || v === undefined || r >= v + RAJADA_FORTE_KMH ? r : null;
  });
}

/** Soma da chuva numa série horária e quantas horas tinham medição (`null` sem nenhuma). */
export function somaChuva(serie: readonly (number | null)[]): { mm: number; horas: number } | null {
  let mm = 0;
  let horas = 0;
  for (const v of serie) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      mm += v;
      horas++;
    }
  }
  return horas ? { mm: Math.round(mm * 10) / 10, horas } : null;
}

/** Direção de onde o vento vem, na rosa de 16 pontos em português ("SE", "NNO"). */
export function rumo(graus: number | null | undefined): string | null {
  if (typeof graus !== 'number' || !Number.isFinite(graus)) return null;
  const nomes = ['N', 'NNE', 'NE', 'ENE', 'L', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
  return nomes[Math.round((((graus % 360) + 360) % 360) / 22.5) % 16]!;
}

export interface Resumo {
  /** Estações na conta (aeródromos só se estiverem no mapa). */
  total: number;
  /** Com a variável medida no quadro. */
  comValor: number;
  maior: { estacao: Estacao; valor: number } | null;
  menor: { estacao: Estacao; valor: number } | null;
  /** Aeródromos com fumaça ou névoa seca no METAR que vale no quadro. */
  fumaca: { estacao: Estacao; leitura: Leitura }[];
}

/** O que o painel diz do quadro: quantas estações mediram, os extremos e a fumaça dos aeródromos. */
export function resumoNoQuadro(
  dados: Pick<Dados, 'estacoes' | 't0' | 'passoMin' | 'slots'>,
  variavel: Variavel,
  instante: number | null,
  comMetar: boolean,
  temFumaca: (wx: string | null | undefined) => boolean,
): Resumo {
  const r: Resumo = { total: 0, comValor: 0, maior: null, menor: null, fumaca: [] };
  for (const e of dados.estacoes) {
    if (e.rede === 'm' && !comMetar) continue;
    r.total++;
    const l = leituraNoQuadro(e, dados, instante);
    if (l && e.rede === 'm' && temFumaca(l.wx)) r.fumaca.push({ estacao: e, leitura: l });
    const v = valorDe(l, variavel);
    if (v === null) continue;
    r.comValor++;
    if (!r.maior || v > r.maior.valor) r.maior = { estacao: e, valor: v };
    if (!r.menor || v < r.menor.valor) r.menor = { estacao: e, valor: v };
  }
  r.fumaca.sort((a, b) => (a.leitura.valores.vis ?? Infinity) - (b.leitura.valores.vis ?? Infinity));
  return r;
}
